/**
 * Admin events list + create/edit/delete.
 *
 * Phase: 5
 *
 * Mantine Table with action column. Create/edit use a single modal driven by
 * `editing` state (null = create mode, EventOut = edit mode). Delete opens a
 * confirmation modal with the cascade option exposed when the event has
 * certificates.
 */

import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Checkbox,
  Group,
  Modal,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
  Title,
  Tooltip,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { IconEdit, IconEye, IconPlus, IconTrash } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  createEvent,
  deleteEvent,
  listEvents,
  updateEvent,
} from "../../api/admin";
import { ApiError } from "../../api/client";
import type { EventOut } from "../../api/types";

interface EventFormValues {
  name: string;
  serial_number: string;
  slug: string;
  event_date: string;
  description: string;
}

export function Events() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [editing, setEditing] = useState<EventOut | null>(null);
  const [formOpened, { open: openForm, close: closeForm }] = useDisclosure(false);
  const [deleteTarget, setDeleteTarget] = useState<EventOut | null>(null);
  const [cascade, setCascade] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const eventsQuery = useQuery({ queryKey: ["events"], queryFn: listEvents });

  const form = useForm<EventFormValues>({
    initialValues: { name: "", serial_number: "", slug: "", event_date: "", description: "" },
    validate: {
      name: (v) => (v.trim().length < 2 ? "İsim çok kısa." : null),
      serial_number: (v) =>
        v.trim().length < 1
          ? "Seri numarası gereklidir."
          : v.trim().length > 64
            ? "Seri numarası en fazla 64 karakter olabilir."
            : null,
    },
  });

  function handleOpenCreate() {
    setEditing(null);
    setFormError(null);
    form.setValues({ name: "", serial_number: "", slug: "", event_date: "", description: "" });
    openForm();
  }

  function handleOpenEdit(ev: EventOut) {
    setEditing(ev);
    setFormError(null);
    form.setValues({
      name: ev.name,
      serial_number: ev.serial_number,
      slug: ev.slug,
      event_date: ev.event_date ?? "",
      description: ev.description ?? "",
    });
    openForm();
  }

  const saveMutation = useMutation({
    mutationFn: async (values: EventFormValues) => {
      const payload = {
        name: values.name.trim(),
        serial_number: values.serial_number.trim(),
        slug: values.slug.trim() || undefined,
        event_date: values.event_date.trim() || null,
        description: values.description.trim() || null,
      };
      return editing
        ? updateEvent(editing.id, payload)
        : createEvent(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["events"] });
      closeForm();
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        const code = (err.body as { error?: string } | null)?.error;
        if (code === "serial_conflict") {
          setFormError("Bu seri numarası zaten kullanılıyor.");
        } else if (code === "slug_conflict") {
          setFormError("Bu slug zaten kullanılıyor.");
        } else {
          setFormError("Bu kayıt zaten mevcut.");
        }
      } else if (err instanceof ApiError && err.status === 422) {
        setFormError(
          (err.body as { detail?: string } | null)?.detail ?? "Geçersiz veri.",
        );
      } else {
        setFormError("Kayıt sırasında bir hata oluştu.");
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!deleteTarget) return;
      return deleteEvent(deleteTarget.id, { cascade });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["events"] });
      setDeleteTarget(null);
      setCascade(false);
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        // Force user to acknowledge cascade
        setCascade(false);
      }
    },
  });

  return (
    <Stack gap="md">
      <Group justify="space-between" align="end">
        <Stack gap={2}>
          <Title order={2}>Etkinlikler</Title>
          <Text c="dimmed" size="sm">
            Etkinlikler ve onlara bağlı sertifikalar burada yönetilir.
          </Text>
        </Stack>
        <Button leftSection={<IconPlus size={16} />} onClick={handleOpenCreate}>
          Yeni Etkinlik
        </Button>
      </Group>

      <Card withBorder shadow="xs" padding={0}>
        <Box style={{ overflowX: "auto" }}>
          <Table verticalSpacing="sm" highlightOnHover striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Seri No</Table.Th>
                <Table.Th>İsim</Table.Th>
                <Table.Th>Slug</Table.Th>
                <Table.Th>Tarih</Table.Th>
                <Table.Th>Sertifika</Table.Th>
                <Table.Th style={{ width: 140 }}>İşlemler</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {eventsQuery.isLoading && (
                <Table.Tr>
                  <Table.Td colSpan={6}>
                    <Text c="dimmed" ta="center" py="md">
                      Yükleniyor…
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
              {eventsQuery.isError && (
                <Table.Tr>
                  <Table.Td colSpan={6}>
                    <Alert color="red">Etkinlikler yüklenemedi.</Alert>
                  </Table.Td>
                </Table.Tr>
              )}
              {eventsQuery.data?.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={6}>
                    <Text c="dimmed" ta="center" py="md">
                      Henüz etkinlik yok. "Yeni Etkinlik" ile ekleyin.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
              {eventsQuery.data?.map((ev) => (
                <Table.Tr key={ev.id}>
                  <Table.Td>
                    <Badge variant="light" radius="sm" style={{ fontFamily: "monospace" }}>
                      {ev.serial_number}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Stack gap={0}>
                      <Text fw={500}>{ev.name}</Text>
                      {ev.description && (
                        <Text size="xs" c="dimmed" lineClamp={1}>
                          {ev.description}
                        </Text>
                      )}
                    </Stack>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" ff="monospace">
                      {ev.slug}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{ev.event_date ?? "—"}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge variant="light">{ev.certificate_count}</Badge>
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4} wrap="nowrap">
                      <Tooltip label="Sertifikaları görüntüle">
                        <ActionIcon
                          variant="subtle"
                          onClick={() => navigate(`/admin/events/${ev.id}`)}
                        >
                          <IconEye size={16} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label="Düzenle">
                        <ActionIcon variant="subtle" onClick={() => handleOpenEdit(ev)}>
                          <IconEdit size={16} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label="Sil">
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          onClick={() => {
                            setDeleteTarget(ev);
                            setCascade(false);
                          }}
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Box>
      </Card>

      {/* Create / Edit modal */}
      <Modal
        opened={formOpened}
        onClose={closeForm}
        title={editing ? "Etkinliği Düzenle" : "Yeni Etkinlik"}
        size="md"
      >
        <form onSubmit={form.onSubmit((values) => saveMutation.mutate(values))}>
          <Stack gap="md">
            <TextInput
              label="İsim"
              placeholder="React Workshop 2026"
              required
              {...form.getInputProps("name")}
            />
            <TextInput
              label="Seri Numarası"
              description="Etkinlik için benzersiz, sertifikalarda yazacak tanıtıcı kod (örn. REACT-2026-03)."
              placeholder="REACT-2026-03"
              required
              {...form.getInputProps("serial_number")}
            />
            <TextInput
              label="Slug"
              description="URL'lerde kullanılır. Boş bırakırsanız isimden türetilir."
              placeholder="react-workshop-2026"
              {...form.getInputProps("slug")}
            />
            <TextInput
              label="Tarih"
              type="date"
              {...form.getInputProps("event_date")}
            />
            <Textarea
              label="Açıklama"
              minRows={2}
              autosize
              {...form.getInputProps("description")}
            />
            {formError && (
              <Alert color="red" variant="light">
                {formError}
              </Alert>
            )}
            <Group justify="end">
              <Button variant="default" onClick={closeForm}>
                İptal
              </Button>
              <Button type="submit" loading={saveMutation.isPending}>
                {editing ? "Güncelle" : "Oluştur"}
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      {/* Delete confirmation */}
      <Modal
        opened={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        title="Etkinliği Sil"
        size="md"
      >
        {deleteTarget && (
          <Stack gap="md">
            <Text>
              <strong>{deleteTarget.name}</strong> etkinliğini silmek üzeresiniz. Bu işlem
              geri alınamaz.
            </Text>
            {deleteTarget.certificate_count > 0 && (
              <Alert color="yellow" variant="light">
                Bu etkinliğe bağlı <strong>{deleteTarget.certificate_count}</strong>{" "}
                sertifika var. Silmek için aşağıdaki kutucuğu işaretleyin.
              </Alert>
            )}
            {deleteTarget.certificate_count > 0 && (
              <Checkbox
                label="Sertifikaları da sil (cascade)"
                checked={cascade}
                onChange={(e) => setCascade(e.currentTarget.checked)}
              />
            )}
            <Group justify="end">
              <Button variant="default" onClick={() => setDeleteTarget(null)}>
                İptal
              </Button>
              <Button
                color="red"
                loading={deleteMutation.isPending}
                disabled={deleteTarget.certificate_count > 0 && !cascade}
                onClick={() => deleteMutation.mutate()}
              >
                Sil
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
}
